// @ts-check
// pwa/components/help-popover.js
// Help & Tutorials popover (aria-haspopup="dialog").
// Opens from the circle-help button in app-header.js.
//
// Usage:
//   wireHelpPopover(headerRoot, me)   — call after every header paint
//   openHelpPopover(triggerBtn, me)   — open programmatically
//   closeHelpPopover()                — close programmatically

import { html, render } from 'lit-html';
import { unsafeHTML } from 'lit-html/directives/unsafe-html.js';
import { icon } from '../lib/icons.js';
import { surfaceTourForPath, getTourTips } from './tip-catalogue.js';
import { isSeen, resetTips, isAutoplaying, setAutoplay } from '../lib/tips-state.js';
import { startTour } from './hub-tips.js';
import { hasAdminArea } from '../lib/store.js';
import { startFullTour } from '../lib/full-tour.js';
import { openShortcutsDialog } from './keyboard-shortcuts.js';

// ── Module state ──────────────────────────────────────────────────────────
let _popoverEl  = /** @type {HTMLElement|null} */ (null);
let _open       = false;
let _triggerBtn = /** @type {HTMLElement|null} */ (null);
let _me         = /** @type {{ is_admin?: boolean } | null} */ (null);

// ── Popover template ──────────────────────────────────────────────────────

/**
 * Build the popover's inner content using lit-html.
 * Renders into the .cm-help-popover container.
 *
 * @param {{ is_admin?: boolean } | null} me
 */
function _popoverTemplate(me) {
  // Context-aware: offer THIS page's tour (if one exists + the audience matches).
  // Admin-surface tours show for admin OR coordinator (anyone who can reach the surface).
  const surface = surfaceTourForPath(location.pathname);
  const showSurfaceTour = !!surface && (surface.audience !== 'admin' || hasAdminArea(me));
  const surfaceDone = showSurfaceTour ? _isTourComplete(surface.tour) : false;

  return html`
    <div class="cm-help-popover__head">
      <p class="cm-help-popover__title">Help &amp; Tutorials</p>
    </div>
    <ul class="cm-help-list" role="list">
      ${showSurfaceTour ? html`
        <li>
          <button class="cm-help-item"
                  @click=${() => _startSurfaceTour(surface.tour)}
                  aria-label="${surfaceDone ? 'Replay' : 'Take'} the ${surface.surface} tour">
            ${unsafeHTML(icon('play-circle', 'w-4 h-4'))}
            ${surfaceDone ? `Replay this page's tour` : `Take this page's tour`}
          </button>
        </li>
      ` : ''}

      <li>
        <button class="cm-help-item" @click=${() => _startFullTour()}
                aria-label="Take the full guided tour across every tab">
          ${unsafeHTML(icon('map', 'w-4 h-4'))}
          Take the full tour
        </button>
      </li>

      ${_finePointer() ? html`
        <li>
          <button class="cm-help-item" @click=${_openShortcuts} aria-keyshortcuts="?">
            ${unsafeHTML(icon('keyboard', 'w-4 h-4'))}
            Keyboard shortcuts
          </button>
        </li>
      ` : ''}

      <li>
        <a href="/help/starter" data-nav class="cm-help-item"
           @click=${() => closeHelpPopover()}>
          ${unsafeHTML(icon('book-open', 'w-4 h-4'))}
          Quick start guide
        </a>
      </li>

      <li>
        <a href="/help/tutorials" data-nav class="cm-help-item"
           @click=${() => closeHelpPopover()}>
          ${unsafeHTML(icon('graduation-cap', 'w-4 h-4'))}
          All tutorials &amp; guides
        </a>
      </li>

      <li role="separator" class="cm-help-sep" aria-hidden="true"></li>

      <li>
        <button class="cm-help-item" @click=${() => _toggleAutoplay()}
                aria-label="${isAutoplaying() ? "Don't show tips automatically" : 'Re-enable automatic tips'}">
          ${unsafeHTML(icon(isAutoplaying() ? 'eye-off' : 'eye', 'w-4 h-4'))}
          ${isAutoplaying() ? "Don't show tips" : 'Show tips'}
        </button>
      </li>

      <li>
        <button class="cm-help-item" @click=${() => _resetTipsAndReplay()}
                aria-label="Reset all tips and tours, then replay this page">
          ${unsafeHTML(icon('rotate-ccw', 'w-4 h-4'))}
          Reset tips &amp; tours
        </button>
      </li>
    </ul>
  `;
}

// ── Tour helpers ──────────────────────────────────────────────────────────

/**
 * Returns true if every tip in the tour has been seen.
 * Fails-open (returns false) if tips-state cache hasn't loaded yet, so the
 * "Start tour" button will still appear rather than being silently hidden.
 */
function _isTourComplete(tourId) {
  try {
    return getTourTips(tourId).every(t => isSeen(t.id));
  } catch {
    return false;
  }
}

function _startSurfaceTour(tourId) {
  closeHelpPopover();
  // force:true so "replay" re-shows the whole tour even if its tips were already seen.
  startTour(tourId, { isAdmin: hasAdminArea(_me), onComplete: null, force: true });
}

/** Keyboard shortcuts only matter with a keyboard + pointer (not on phones). */
function _finePointer() {
  try { return window.matchMedia('(pointer: fine)').matches; } catch { return true; }
}

function _openShortcuts() {
  const back = _triggerBtn;
  closeHelpPopover();
  openShortcutsDialog(back);   // focus returns to the Help button on close
}

function _startFullTour() {
  closeHelpPopover();
  startFullTour();   // walks Dashboard → Sign up → Resources → History, auto-navigating
}

async function _toggleAutoplay() {
  await setAutoplay(!isAutoplaying());
  // Re-render popover in-place so the label and icon reflect the new state.
  if (_popoverEl && _open) render(_popoverTemplate(_me), _popoverEl);
}

/**
 * Reset ALL tips/tours to their never-seen state (server + local), re-arm the
 * once-per-session auto-play guard (via the 'hubtips:reset' event app.js listens for),
 * and immediately replay the current surface's tour so the reset is visibly confirmed.
 * Every surface's tour will then auto-play again on its next visit.
 */
async function _resetTipsAndReplay() {
  closeHelpPopover();
  try { await resetTips(); } catch { /* best-effort; localStorage fallback still cleared */ }
  // Let app.js clear its per-session "already auto-played" set so tours fire again on nav.
  try { window.dispatchEvent(new CustomEvent('hubtips:reset')); } catch { /* older browsers */ }
  // Immediate feedback: replay this page's tour now if it has one.
  const surface = surfaceTourForPath(location.pathname);
  if (surface && (surface.audience !== 'admin' || hasAdminArea(_me))) {
    startTour(surface.tour, { isAdmin: hasAdminArea(_me), onComplete: null, force: true });
  }
}

// ── Popover lifecycle ─────────────────────────────────────────────────────

/**
 * Create or obtain the popover element anchored inside the trigger's
 * nearest positioned ancestor (the header bar).
 *
 * @param {HTMLElement} triggerBtn
 */
function _ensurePopover(triggerBtn) {
  if (_popoverEl && _popoverEl.isConnected) return _popoverEl;

  _popoverEl = document.createElement('div');
  _popoverEl.className = 'cm-help-popover';
  _popoverEl.setAttribute('role', 'dialog');
  _popoverEl.setAttribute('aria-label', 'Help & Tutorials');
  _popoverEl.setAttribute('id', 'cm-help-popover');

  // Anchor inside the trigger's parent so `position:absolute` resolves to it
  const parent = triggerBtn.closest('.app-header') || triggerBtn.parentElement || document.body;
  // Parent needs relative positioning for the popover to anchor correctly.
  // app-header.css already sets position:relative on .app-header; no patch needed.
  parent.appendChild(_popoverEl);

  return _popoverEl;
}

/** Open the help popover. */
export function openHelpPopover(triggerBtn, me) {
  _triggerBtn = triggerBtn;
  _me = me;

  const pop = _ensurePopover(triggerBtn);

  render(_popoverTemplate(me), pop);
  pop.classList.add('open');
  _open = true;

  triggerBtn.setAttribute('aria-expanded', 'true');

  // Move focus to first focusable item
  requestAnimationFrame(() => {
    const first = /** @type {HTMLElement|null} */ (
      pop.querySelector('a,button')
    );
    first?.focus();
  });

  // Dismiss on outside click or Escape
  setTimeout(() => {
    document.addEventListener('click', _onDocClick, { once: false, capture: true });
    document.addEventListener('keydown', _onDocKey, { capture: true });
  }, 0);
}

/** Close the help popover. */
export function closeHelpPopover() {
  if (!_open) return;
  _open = false;

  if (_popoverEl) _popoverEl.classList.remove('open');
  if (_triggerBtn) {
    _triggerBtn.setAttribute('aria-expanded', 'false');
    _triggerBtn.focus();
  }

  document.removeEventListener('click', _onDocClick, { capture: true });
  document.removeEventListener('keydown', _onDocKey, { capture: true });
}

/** Toggle help popover. */
function _toggle(triggerBtn, me) {
  if (_open) {
    closeHelpPopover();
  } else {
    openHelpPopover(triggerBtn, me);
  }
}

// ── Document event handlers ───────────────────────────────────────────────

function _onDocClick(e) {
  if (!_open) return;
  const target = /** @type {Node} */ (e.target);
  const pop = document.getElementById('cm-help-popover');
  // Click outside both the trigger and the popover
  if (
    pop && !pop.contains(target) &&
    _triggerBtn && !_triggerBtn.contains(target)
  ) {
    closeHelpPopover();
  }
}

function _onDocKey(e) {
  if (!_open) return;
  if (e.key === 'Escape') {
    e.preventDefault();
    e.stopPropagation();
    closeHelpPopover();
  }
}

// ── Wire function (called after every header paint) ───────────────────────

/**
 * Wire the help button inside the header root.
 * Uses .onclick to avoid stacking listeners across re-paints (same idiom as
 * app-header.js / notification-popover.js).
 *
 * @param {HTMLElement} root  header container element
 * @param {{ is_admin?: boolean } | null} me
 */
export function wireHelpPopover(root, me) {
  _me = me;
  const btn = /** @type {HTMLElement|null} */ (root.querySelector('[data-help-open]'));
  if (!btn) return;
  btn.onclick = () => _toggle(btn, me);
}
